/* Livre 3D en volume réel (mi-book3d) : couverture, dos, tranche des pages, pivot au défilement, inclinaison au pointeur, glisse au doigt.
   Origine : classe Book3D de js/signature.js (maquette, « moment signature » du directeur du mouvement), reprise ici sans dépendance à i18n.js, cart.js ni aux tables de la maquette :
   toutes les données viennent du balisage [data-book3d] imprimé par le snippet mi-book-volume (data-cover : couverture à plat ; data-spine / data-ink : couleurs ; data-pages : épaisseur de la tranche ;
   data-title, data-tagline, data-author : dos et quatrième de couverture). Sans data-cover, rien n'est fait : l'image 3D à fond transparent (repli) reste affichée.
   Un livre déjà amélioré par assets/mi-signature.js (classe is-3d) n'est jamais traité deux fois.
   Garde-fous : prefers-reduced-motion (pose fixe, aucun défilement lié, le geste reste possible), éditeur de thème (Shopify.designMode : rien), transform et opacité seulement,
   la boucle ne tourne que pour les livres visibles, CLS 0 (conteneur à rapport fixe, CSS de assets/mi-signature.css). Aucune dépendance. */
const reduceMQ = matchMedia('(prefers-reduced-motion: reduce)');
const designMode = !!(window.Shopify && window.Shopify.designMode);
const fine = matchMedia('(hover: hover) and (pointer: fine)').matches;
const $ = (s, r = document) => r.querySelector(s);
const $$ = (s, r = document) => [...r.querySelectorAll(s)];
const clamp = (x, a, b) => Math.min(b, Math.max(a, x));
const lerp = (a, b, k) => a + (b - a) * k;
const rad = (d) => d * Math.PI / 180;
const POSE = { yaw: 14, pitch: -6 };   // leçon 12 : quasi de face, dos visible à gauche, caméra légèrement au-dessus

const books = new Set();
const bookIO = 'IntersectionObserver' in window ? new IntersectionObserver((es) => es.forEach(e => { e.target._b.visible = e.isIntersecting; if (e.isIntersecting) kick(); }), { rootMargin: '120px 0px' }) : null;
let looping = false;
function kick() { if (!looping) { looping = true; requestAnimationFrame(loop); } }

const esc = (s) => String(s ?? '').replace(/[&<>"]/g, c => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;' }[c]));

class Book3D {
  constructor(el) {
    this.el = el; this.mode = el.dataset.b3dMode || 'scroll';
    const h = el.dataset.handle; const D = {};
    this.handle = h;
    this.cover = el.dataset.cover || D.cover; this.spine = el.dataset.spine || D.spine || '#4a2a1a'; this.ink = el.dataset.ink || D.ink || '#fff';
    this.pages = +el.dataset.pages || 120; this.ratio = +el.dataset.ratio || D.ratio || 1.6;
    this.yaw = POSE.yaw; this.pitch = POSE.pitch; this.vyaw = 0;
    this.drag = 0; this.dragPitch = 0; this.hx = 0; this.hy = 0; this.curHX = 0; this.curHY = 0;
    this.p = .5; this.visible = true; this.last = 0; this.auto = this.mode === 'spin'; this.userTouched = false; this.idleT = 0;
    this.build(); this.bind(); this.layout();
    books.add(this); el._b = this; bookIO ? bookIO.observe(el) : (this.visible = true);
    el.classList.add('is-3d'); kick();
  }
  build() {
        const title = this.el.dataset.title || '';
    const sub = this.el.dataset.tagline || '';
    const author = this.el.dataset.author || '';
        const st = document.createElement('div'); st.className = 'b3d__stage'; st.tabIndex = 0; st.setAttribute('role', 'img');
    st.setAttribute('aria-label', title);
    const sh = '<i class="b3d__shade"></i>';
    st.innerHTML = `<div class="b3d__halo" aria-hidden="true"></div><div class="b3d__shadow" aria-hidden="true"></div>
<div class="b3d__rot" aria-hidden="true">
 <div class="b3d__f b3d__front"><span class="b3d__gloss"></span>${sh}</div>
 <div class="b3d__f b3d__back"><span class="b3d__back-t">${esc(title)}</span><span class="b3d__back-r"></span><span class="b3d__back-s">${esc(sub)}</span><span class="b3d__back-a">${esc(author)}</span>${sh}</div>
 <div class="b3d__f b3d__spine"><span class="b3d__spine-t">${esc(title)}</span><svg class="b3d__spine-m" viewBox="0 0 24 24" aria-hidden="true"><path fill="currentColor" d="M12 1l1.9 8.1L22 12l-8.1 2.9L12 23l-1.9-8.1L2 12l8.1-2.9z"/></svg>${sh}</div>
 <div class="b3d__f b3d__pg b3d__right">${sh}</div><div class="b3d__f b3d__top">${sh}</div><div class="b3d__f b3d__bottom">${sh}</div>
</div>`;
    this.el.appendChild(st); this.stage = st; this.rot = $('.b3d__rot', st); this.shadow = $('.b3d__shadow', st); this.gloss = $('.b3d__gloss', st);
    this.shades = { front: $('.b3d__front > .b3d__shade', st), back: $('.b3d__back > .b3d__shade', st), spine: $('.b3d__spine > .b3d__shade', st), right: $('.b3d__right > .b3d__shade', st), top: $('.b3d__top > .b3d__shade', st), bottom: $('.b3d__bottom > .b3d__shade', st) };
    this.el.style.setProperty('--cover', `url("${this.cover}")`);
    this.el.style.setProperty('--spine', this.spine); this.el.style.setProperty('--ink-on', this.ink);
    const im = new Image(); im.onload = () => { if (im.naturalWidth) { this.ratio = im.naturalHeight / im.naturalWidth; this.layout(); } }; im.src = this.cover;
  }
  layout() {
    const W = this.el.clientWidth, H = this.el.clientHeight; if (!W || !H) return;
    const hh = Math.min(H * .80, W * .72 * this.ratio); const w = hh / this.ratio;
    const tmm = 1.5 + this.pages * .065;   // épaisseur en mm d'après le nombre de pages (papier 80 g + deux couvertures)
    const th = w * tmm / 140;              // largeur de référence d'un livre broché : 140 mm
    const s = this.el.style; s.setProperty('--w', w.toFixed(1) + 'px'); s.setProperty('--h', hh.toFixed(1) + 'px'); s.setProperty('--t', Math.max(7, th).toFixed(1) + 'px');
    this.w = w; this.hh = hh; this.th = Math.max(7, th); this.dirty = true;
  }
  bind() {
    const el = this.el, st = this.stage;
    this.ro = 'ResizeObserver' in window ? new ResizeObserver(() => this.layout()) : null; this.ro && this.ro.observe(el);
    let sx = 0, sy = 0, lx = 0, lt = 0, down = false, moved = false;
    st.addEventListener('pointerdown', (e) => {
      if (e.pointerType === 'mouse' && e.button !== 0) return;
      down = true; moved = false; sx = lx = e.clientX; sy = e.clientY; lt = performance.now(); this.vyaw = 0; this.auto = false; this.userTouched = true; this.releaseAt = 0;
      try { st.setPointerCapture(e.pointerId); } catch (_) {}
      el.classList.add('is-drag'); this.hideHint && this.hideHint(); kick();
    });
    st.addEventListener('pointermove', (e) => {
      if (e.pointerType === 'mouse' && !down) { const r = el.getBoundingClientRect(); this.hx = ((e.clientX - r.left) / r.width - .5) * 2; this.hy = ((e.clientY - r.top) / r.height - .5) * 2; kick(); return; }
      if (!down) return;
      const dx = e.clientX - lx; lx = e.clientX; if (Math.abs(e.clientX - sx) > 4) moved = true;
      const dt = Math.max(8, performance.now() - lt); lt = performance.now();
      const k = this.mode === 'spin' ? .55 : .42; this.drag += dx * k; this.vyaw = lerp(this.vyaw, dx * k / dt * 16, .5);
      if (this.mode === 'spin') this.dragPitch = clamp(this.dragPitch + (e.clientY - sy) * 0.002, -8, 8);
      kick();
    });
    const up = () => { if (!down) return; down = false; el.classList.remove('is-drag'); this.releaseAt = performance.now(); kick(); };
    st.addEventListener('pointerup', up); st.addEventListener('pointercancel', up);
    st.addEventListener('pointerleave', (e) => { if (e.pointerType === 'mouse') { this.hx = 0; this.hy = 0; kick(); } });
    st.addEventListener('keydown', (e) => { const d = e.key === 'ArrowLeft' ? -18 : e.key === 'ArrowRight' ? 18 : 0; if (d) { e.preventDefault(); this.drag += d; this.auto = false; this.hideHint && this.hideHint(); kick(); } });
  }
  /* pose cible selon le mode */
  target(now) {
    const reduce = reduceMQ.matches; let yaw = POSE.yaw + this.drag, pitch = POSE.pitch;
    if (this.mode === 'scroll') {
      const r = this.el.getBoundingClientRect(); const vh = innerHeight; const p = clamp((vh - r.top) / (vh + r.height), 0, 1); this.p = p;
      const amp = innerWidth < 768 ? .6 : 1;
      if (!reduce) { yaw += (.5 - p) * 52 * amp; pitch += (p - .5) * 5; }
      if (!reduce && fine) { yaw += this.curHX * 13; pitch += this.curHY * -7; }
      if (!reduce) yaw += Math.sin(now / 2600) * 1.4;
      // retour en douceur après un geste (le défilement reprend la main)
      if (this.releaseAt && !reduce && now - this.releaseAt > 900) this.drag = lerp(this.drag, 0, .045);
      if (reduce && this.releaseAt === 0) { /* pose fixe, le geste reste possible */ }
    } else {
      if (this.auto && !reduce) yaw += now / 90;
      if (!this.auto || reduce) { yaw += 0; }
      pitch += this.dragPitch + (fine ? this.curHY * -4 : 0);
    }
    return { yaw, pitch };
  }
  step(now) {
    const dtF = Math.min(2.5, (now - (this.last || now)) / 16.7); this.last = now;
    this.curHX = lerp(this.curHX, this.hx, .08 * dtF); this.curHY = lerp(this.curHY, this.hy, .08 * dtF);
    if (this.mode === 'spin') {
      // inertie après lâcher
      if (!this.el.classList.contains('is-drag') && Math.abs(this.vyaw) > .02 && !reduceMQ.matches) { this.drag += this.vyaw * dtF; this.vyaw *= Math.pow(.95, dtF); }
      if (this.auto && !reduceMQ.matches) this.autoYaw = (this.autoYaw || 0) + .12 * dtF;
    }
    const tg = this.target(now);
    const yawT = tg.yaw + (this.mode === 'spin' && this.auto && !reduceMQ.matches ? 0 : 0);
    const k = reduceMQ.matches ? 1 : this.mode === 'spin' ? Math.min(1, .35 * dtF) : Math.min(1, .12 * dtF);
    this.yaw = this.mode === 'spin' && this.el.classList.contains('is-drag') ? yawT : lerp(this.yaw, yawT, k);
    this.pitch = lerp(this.pitch, tg.pitch, k);
    this.render();
  }
  render() {
    const yaw = this.yaw + (this.mode === 'spin' ? (this.autoYaw || 0) : 0), pitch = this.pitch;
    this.rot.style.transform = `rotateX(${pitch.toFixed(2)}deg) rotateY(${yaw.toFixed(2)}deg)`;
    // éclairage : lumière en haut à gauche, légèrement devant
    const L = [-.36, -.46, .81]; const ly = rad(yaw), lx = rad(pitch);
    const rotN = (n) => { // Ry puis Rx (comme la transformation CSS)
      let [x, y, z] = n; let x1 = x * Math.cos(ly) + z * Math.sin(ly), z1 = -x * Math.sin(ly) + z * Math.cos(ly);
      let y2 = y * Math.cos(lx) - z1 * Math.sin(lx), z2 = y * Math.sin(lx) + z1 * Math.cos(lx); return [x1, y2, z2];
    };
    const N = { front: [0, 0, 1], back: [0, 0, -1], spine: [-1, 0, 0], right: [1, 0, 0], top: [0, -1, 0], bottom: [0, 1, 0] };
    for (const f in N) { const n = rotN(N[f]); const lit = clamp(n[0] * L[0] + n[1] * L[1] + n[2] * L[2], 0, 1); const op = (1 - lit) * (f === 'front' ? .26 : .55); this.shades[f].style.setProperty('--sh', op.toFixed(3)); }
    // reflet qui balaie la couverture
    const gl = clamp((yaw - POSE.yaw) / 60, -1, 1); this.gloss.style.transform = `translateX(${(-30 - gl * 220).toFixed(0)}%) skewX(-12deg)`;
    // ombre au sol : largeur d'empreinte, décalage opposé à la lumière
    const foot = (this.w * Math.abs(Math.cos(ly)) + this.th * Math.abs(Math.sin(ly))) / (this.w * 1.25);
    const off = Math.sin(ly) * -this.w * .10 + this.w * .06;
    this.shadow.style.transform = `translateX(${off.toFixed(1)}px) scaleX(${(foot * 1.05).toFixed(3)})`;
    this.shadow.style.opacity = (.72 + .28 * Math.abs(Math.cos(ly))).toFixed(2);
  }
  settled() { return false; }
}
function loop(now) {
  let any = false;
  books.forEach(b => { if (!b.el.isConnected) { books.delete(b); b.ro && b.ro.disconnect(); return; } if (b.visible) { b.step(now); any = true; } });
  if (any && !document.hidden) requestAnimationFrame(loop); else looping = false;
}
document.addEventListener('visibilitychange', () => { if (!document.hidden) kick(); });
reduceMQ.addEventListener?.('change', () => { books.forEach(b => b.layout()); kick(); });

function upgradeBooks(rootEl = document) {
  if (designMode) return;
  $$('[data-book3d]:not(.is-3d)', rootEl).forEach((el) => {
    if (el._building || !el.dataset.cover) return;   // sans couverture à plat : l'image 3D de repli reste affichée
    el._building = true;
    try { new Book3D(el); } catch (e) { console.warn('[book3d] livre 3D désactivé', e); el._building = false; }
  });
}

function scan(scope) { upgradeBooks(scope || document); }
scan();
document.addEventListener('shopify:section:load', (event) => scan(event.target));
document.addEventListener('DOMContentLoaded', () => scan());
